import {
  PayoutMethodType,
  type EstimateOption,
  type PayoutInput,
} from "../interfaces/payout";
import type { RecipientDestination } from "../api/recipients";

/**
 * Recipient-book destination replay helpers — gallery mirror of flow-send.
 */

/** Strips a country prefix from a book `stateCode` when present (`MX-JAL` → `JAL`). */
export const bareStateCode = (
  stateCode: string | undefined | null
): string | null => {
  if (!stateCode?.trim()) return null;
  const trimmed = stateCode.trim().toUpperCase();
  const dash = trimmed.indexOf("-");
  if (dash > 0 && dash <= 3) return trimmed.slice(dash + 1) || null;
  return trimmed;
};

export const destinationsForIntent = (
  destinations: RecipientDestination[],
  intent: PayoutMethodType | null
): RecipientDestination[] => {
  if (!intent) return [];
  return destinations.filter((d) => d.payoutMethod === intent);
};

export const mapDestinationToPayoutInput = (
  destination: RecipientDestination,
  intent: PayoutMethodType
): PayoutInput | null => {
  if (destination.payoutMethod !== intent) return null;

  switch (intent) {
    case PayoutMethodType.CASH_PICKUP: {
      const stateCode = bareStateCode(destination.cash?.stateCode);
      const payerId = destination.cash?.payerCode?.trim() || null;
      if (!stateCode || !payerId) return null;
      return { source: "saved", stateCode, payerId };
    }
    case PayoutMethodType.BANK_ACCOUNT: {
      if (!destination.paymentMethodId) return null;
      const last4 =
        destination.display?.maskedAccount?.replace(/\D/g, "").slice(-4) ||
        undefined;
      return {
        source: "saved",
        paymentMethodId: destination.paymentMethodId,
        bankId: destination.display?.bankName ?? undefined,
        last4,
      };
    }
    case PayoutMethodType.PUSH_TO_CARD: {
      if (!destination.paymentMethodId) return null;
      return {
        source: "saved",
        paymentMethodId: destination.paymentMethodId,
        brand: destination.display?.cardBrand ?? "unknown",
        last4: destination.display?.cardLast4 ?? "0000",
      };
    }
    case PayoutMethodType.MOBILE_WALLET: {
      const phone = destination.phone?.trim();
      if (!phone) return null;
      return { source: "saved", phone, carrierId: undefined };
    }
    default:
      return null;
  }
};

const payerCodeOf = (
  payout: PayoutInput,
  method: PayoutMethodType
): string | null => {
  switch (method) {
    case PayoutMethodType.BANK_ACCOUNT:
      return payout.bankId ?? null;
    case PayoutMethodType.CASH_PICKUP:
      return payout.payerId ?? null;
    case PayoutMethodType.MOBILE_WALLET:
      return payout.carrierId ?? null;
    default:
      return null;
  }
};

export const resolveCapabilityKeyForPayout = (
  method: PayoutMethodType,
  payout: PayoutInput,
  options: EstimateOption[]
): string | null => {
  const forMethod = options.filter((o) => o.payoutMethod === method);
  if (forMethod.length === 0) return null;

  const payerCode = payerCodeOf(payout, method);
  if (payerCode) {
    const byPayer = forMethod.find((o) => o.payerCode === payerCode);
    if (byPayer) return byPayer.capabilityKey;
    if (
      method === PayoutMethodType.CASH_PICKUP ||
      method === PayoutMethodType.MOBILE_WALLET
    ) {
      return null;
    }
  }

  if (
    (method === PayoutMethodType.BANK_ACCOUNT ||
      method === PayoutMethodType.PUSH_TO_CARD) &&
    (payout.source === "saved" || payout.paymentMethodId)
  ) {
    return forMethod[0]?.capabilityKey ?? null;
  }

  return forMethod[0]?.capabilityKey ?? null;
};

export interface CompatibleBookDestination {
  destination: RecipientDestination;
  payout: PayoutInput;
  capabilityKey: string;
}

export const compatibleBookDestinations = (
  destinations: RecipientDestination[],
  intent: PayoutMethodType | null,
  quoteOptions: EstimateOption[]
): CompatibleBookDestination[] => {
  if (!intent) return [];
  const out: CompatibleBookDestination[] = [];
  for (const destination of destinationsForIntent(destinations, intent)) {
    const payout = mapDestinationToPayoutInput(destination, intent);
    if (!payout) continue;
    const capabilityKey = resolveCapabilityKeyForPayout(
      intent,
      payout,
      quoteOptions
    );
    if (!capabilityKey) continue;
    out.push({ destination, payout, capabilityKey });
  }
  return out;
};

export const destinationListKey = (destination: RecipientDestination): string => {
  if (destination.paymentMethodId) return `pm:${destination.paymentMethodId}`;
  if (destination.cash) {
    return `cash:${destination.payoutMethod}:${destination.cash.stateCode ?? ""}:${destination.cash.payerCode ?? ""}`;
  }
  if (destination.phone) return `wallet:${destination.phone}`;
  return `method:${destination.payoutMethod}:${destination.lastUsedAt}`;
};
